import React, { useCallback, useRef, useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { ElementsLayer } from "./ElementRenderer";
import { useCanvasDrag } from "./hooks/useCanvasDrag";
import { ZoomIn, ZoomOut, Maximize2, Minimize2, Hand } from "lucide-react";
import { useFreeDraw, usePenTool, useShapeDraw } from "./drawing";
import SelectionOverlay from "./SelectionOverlay";
import MultiSelectionOverlay from "./MultiSelectionOverlay";
import LineEditOverlay from "./LineEditOverlay";
import TextEditOverlay from "./TextEditOverlay";
import ImageCropOverlay from "./ImageCropOverlay";
import { isLineEditable, parseLineModel, serializeLineModel, foldNearest } from "./lineEdit";
import { bboxOf, rectsIntersect, unionBbox } from "./canvasUtils";

const GRID = 22;

export default function Canvas({
  elements,
  selectedIds = [],
  onSelect,
  onMarquee,
  resolveDragTargets,
  onLiveUpdate,
  onLiveUpdateLine,
  onLiveUpdateMany,
  onDragStart,
  onDragEnd,
  onCanvasDrop,
  playing,
  background = "transparent",
  tool = "select",
  onCommitDrawn,
  onCommitShape,
  onEditCommit,
  onResizeStart,
  replayNonce,
  generatingIds,
  apiRef,
  onToggleExpand,
  canvasExpanded = false,
  editingTextEl = null,
  onTextCreate,
  onStartTextEdit,
  onTextCommit,
  cropId = null,
  onEnterCrop,
  onExitCrop,
  onDuplicateForDrag,
  onBringToFront,
}) {
  // svgRef points at the transformed content <g>; getSvgPoint maps screen ->
  // artboard coordinates through its CTM, so interactions work anywhere on the
  // (infinite) surface, not just inside the artboard rectangle.
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [marquee, setMarquee] = useState(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const spaceRef = useRef(false);
  // Keep latest elements available to the imperative API without re-creating it.
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  // RAF handle for smooth, animated view transitions (recenter / fit-content).
  const viewAnimRef = useRef(null);

  const { startDrag, getSvgPoint } = useCanvasDrag({ svgRef, onLiveUpdateMany, onDragStart, onDragEnd });

  // Tween the pan/zoom view from its current state to a target so "focus" and
  // "fit" glide into place instead of snapping. Any in-flight tween is cancelled
  // first; a manual pan/zoom that calls setView directly also visually wins.
  const animateView = useCallback((target, duration = 460) => {
    if (viewAnimRef.current) cancelAnimationFrame(viewAnimRef.current);
    const start = { ...viewRef.current };
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const e = ease(t);
      setView({
        scale: start.scale + (target.scale - start.scale) * e,
        tx: start.tx + (target.tx - start.tx) * e,
        ty: start.ty + (target.ty - start.ty) * e,
      });
      if (t < 1) viewAnimRef.current = requestAnimationFrame(step);
      else viewAnimRef.current = null;
    };
    viewAnimRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => () => { if (viewAnimRef.current) cancelAnimationFrame(viewAnimRef.current); }, []);

  const isDrawTool = tool === "pen" || tool === "pencil";

  const freeDraw = useFreeDraw({
    tool,
    getSvgPoint,
    onCommit: onCommitDrawn,
    strokeColor: "#FF6B00",
    strokeWidth: 3,
    fill: "none",
  });
  const penTool = usePenTool({
    tool,
    getSvgPoint,
    onCommit: onCommitDrawn,
    strokeColor: "#FF6B00",
    strokeWidth: 3,
    fill: "#FF6B00",
  });
  const shapeDraw = useShapeDraw({ tool, getSvgPoint, onCommit: onCommitShape });
  const isShapeTool = shapeDraw.isShapeTool;

  const computeFit = useCallback((bb) => {
    const vp = viewportRef.current;
    const pad = 120;
    const w = Math.max(bb.w, 40);
    const h = Math.max(bb.h, 40);
    const s = Math.min((vp.clientWidth - pad) / w, (vp.clientHeight - pad) / h, 1.4);
    const scale = Math.max(0.2, s);
    return {
      scale,
      tx: (vp.clientWidth - w * scale) / 2 - bb.x * scale,
      ty: (vp.clientHeight - h * scale) / 2 - bb.y * scale,
    };
  }, []);

  const fitToRegion = useCallback((bb, animate = false) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const target = computeFit(bb);
    if (animate) animateView(target);
    else setView(target);
  }, [computeFit, animateView]);

  // Initial view, set once on mount (instant, no animation).
  useEffect(() => {
    fitToRegion({ x: 0, y: 0, w: 800, h: 560 });
  }, [fitToRegion]);

  // Keep content visually pinned to its on-screen position when the viewport
  // shifts/resizes (e.g. side panels collapse/expand via the expand button).
  // The pan offset (tx/ty) lives in the surface's own coordinate frame, so when
  // the surface's top-left corner moves we counter-translate the view by exactly
  // that delta — the content never drifts or re-centres, it just stays put while
  // the panel slides smoothly out from under it. flushSync commits the offset in
  // the same frame as the layout change so content can never lag/jitter behind
  // the animating panel.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const r0 = vp.getBoundingClientRect();
    let prev = { left: r0.left, top: r0.top };
    const ro = new ResizeObserver(() => {
      const r = vp.getBoundingClientRect();
      const dLeft = r.left - prev.left;
      const dTop = r.top - prev.top;
      if (dLeft === 0 && dTop === 0) return;
      prev = { left: r.left, top: r.top };
      flushSync(() => setView((v) => ({ ...v, tx: v.tx - dLeft, ty: v.ty - dTop })));
    });
    ro.observe(vp);
    return () => ro.disconnect();
  }, []);

  // Expose imperative helpers to the parent (e.g. for centered shape creation
  // and the "focus / recenter" menu action + shortcut).
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      getCenter: () => {
        const vp = viewportRef.current;
        const v = viewRef.current;
        if (!vp) return { x: 400, y: 280 };
        return {
          x: (vp.clientWidth / 2 - v.tx) / v.scale,
          y: (vp.clientHeight / 2 - v.ty) / v.scale,
        };
      },
      // Pan (without changing zoom) so the content centre — or the artboard
      // origin when the canvas is empty — sits in the middle of the viewport.
      // Glides into place via a tween instead of snapping.
      recenter: () => {
        const vp = viewportRef.current;
        if (!vp) return;
        const v = viewRef.current;
        const els = elementsRef.current;
        const target = els.length
          ? (() => { const b = unionBbox(els); return { x: b.x + b.w / 2, y: b.y + b.h / 2 }; })()
          : { x: 400, y: 280 };
        animateView({
          scale: v.scale,
          tx: vp.clientWidth / 2 - target.x * v.scale,
          ty: vp.clientHeight / 2 - target.y * v.scale,
        });
      },
      // Zoom + pan to frame all content (or a default region when empty).
      fitContent: () => {
        const els = elementsRef.current;
        fitToRegion(els.length ? unionBbox(els) : { x: 0, y: 0, w: 800, h: 560 }, true);
      },
    };
  }, [apiRef, fitToRegion, animateView]);

  const zoomAt = useCallback((factor, clientX, clientY) => {
    setView((v) => {
      const vp = viewportRef.current;
      const rect = vp.getBoundingClientRect();
      const px = clientX == null ? rect.width / 2 : clientX - rect.left;
      const py = clientY == null ? rect.height / 2 : clientY - rect.top;
      const ns = Math.min(8, Math.max(0.1, v.scale * factor));
      const k = ns / v.scale;
      return { scale: ns, tx: px - (px - v.tx) * k, ty: py - (py - v.ty) * k };
    });
  }, []);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        zoomAt(e.deltaY < 0 ? 1.12 : 0.89, e.clientX, e.clientY);
      } else {
        setView((v) => ({ ...v, tx: v.tx - e.deltaX, ty: v.ty - e.deltaY }));
      }
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space") spaceRef.current = true;
    };
    const up = (e) => {
      if (e.code === "Space") spaceRef.current = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const handleElementPointerDown = useCallback(
    (e, el) => {
      if (spaceRef.current || e.button === 1) return;
      if (isDrawTool || isShapeTool) return; // ignore element picks while drawing
      // With the text tool: clicking an existing text edits it in place;
      // clicking anything else falls through so a new text is created.
      if (tool === "text") {
        if (el.type === "text") {
          e.stopPropagation();
          onStartTextEdit?.(el);
        }
        return;
      }
      e.stopPropagation();
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;
      const targets = resolveDragTargets(el.id, additive);
      // Alt/Opt + drag duplicates the (resolved) selection and drags the copies
      // instead of moving the originals. The copies are created at the same
      // position, so the drag delta moves them away from the source.
      if (e.altKey && onDuplicateForDrag) {
        const copies = onDuplicateForDrag(targets);
        if (copies && copies.length) startDrag(e, copies);
        return;
      }
      // A plain click on an element brings it (and its selection group) to the
      // top of the paint order so it renders above siblings. Done before the
      // drag starts so the moved element is already on top while dragging.
      if (!additive && onBringToFront) onBringToFront(targets.map((t) => t.id));
      // Additive clicks only toggle selection; a plain click also begins a drag
      // (which moves the whole current selection / group).
      if (!additive) startDrag(e, targets);
    },
    [resolveDragTargets, startDrag, isDrawTool, isShapeTool, tool, onStartTextEdit, onDuplicateForDrag, onBringToFront]
  );

  // Double-clicking a line-editable element folds the nearest segment at its
  // midpoint (insert a draggable corner). Repeated double-clicks add more
  // folds, turning the line into a multi-segment polyline.
  const handleElementDoubleClick = useCallback(
    (e, el) => {
      if (isDrawTool || isShapeTool) return;
      // Alt/Opt + double-click a selected image enters crop mode.
      if (el.type === "image" && e.altKey) {
        e.stopPropagation();
        e.preventDefault();
        onSelect(el.id);
        onEnterCrop?.(el.id);
        return;
      }
      // Double-click a text element to (re)enter inline edit mode.
      if (el.type === "text") {
        e.stopPropagation();
        e.preventDefault();
        onStartTextEdit?.(el);
        return;
      }
      if (tool !== "select") return;
      if (!isLineEditable(el)) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect(el.id);
      const p = getSvgPoint(e.clientX, e.clientY);
      const model = parseLineModel(el);
      const next = foldNearest(model, p, 40 / viewRef.current.scale);
      const patch = serializeLineModel(next, el);
      onEditCommit?.(el.id, { ...patch, lineEdit: true });
    },
    [tool, isDrawTool, isShapeTool, getSvgPoint, onSelect, onEditCommit, onStartTextEdit, onEnterCrop]
  );

  const startPan = useCallback((e) => {
    const start = { x: e.clientX, y: e.clientY, tx: viewRef.current.tx, ty: viewRef.current.ty };
    const onMove = (ev) => {
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      setView((v) => ({ ...v, tx: start.tx + dx, ty: start.ty + dy }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  // Drag empty space with the Select tool to marquee-select objects.
  const startMarquee = useCallback(
    (e) => {
      const p0 = getSvgPoint(e.clientX, e.clientY);
      let moved = false;
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;
      setMarquee({ x: p0.x, y: p0.y, w: 0, h: 0 });
      const onMove = (ev) => {
        const p = getSvgPoint(ev.clientX, ev.clientY);
        if (Math.abs(p.x - p0.x) + Math.abs(p.y - p0.y) > 2) moved = true;
        setMarquee({
          x: Math.min(p0.x, p.x),
          y: Math.min(p0.y, p.y),
          w: Math.abs(p.x - p0.x),
          h: Math.abs(p.y - p0.y),
        });
      };
      const onUp = (ev) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        const p = getSvgPoint(ev.clientX, ev.clientY);
        const rect = {
          x: Math.min(p0.x, p.x),
          y: Math.min(p0.y, p.y),
          w: Math.abs(p.x - p0.x),
          h: Math.abs(p.y - p0.y),
        };
        setMarquee(null);
        if (!moved) {
          if (!additive) onSelect(null);
          return;
        }
        const hit = elements.filter((el) => rectsIntersect(rect, bboxOf(el))).map((el) => el.id);
        onMarquee(hit, additive);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [getSvgPoint, elements, onMarquee, onSelect]
  );

  // Empty-surface pointer down. Bound to the full-viewport surface so panning,
  // marquee-selection and drawing all work across the entire infinite canvas.
  const handleBgPointerDown = useCallback(
    (e) => {
      // While cropping, background clicks should not start a marquee or clear
      // the selection — double-click on empty space exits crop mode instead.
      if (cropId) return;
      // Text tool: click on empty surface to insert a text cursor at that point.
      if (tool === "text") {
        if (e.button === 1) return;
        const p = getSvgPoint(e.clientX, e.clientY);
        onTextCreate?.(p);
        return;
      }
      if (tool === "pencil") {
        freeDraw.onPointerDown(e);
        return;
      }
      if (tool === "pen") {
        penTool.onPointerDown(e);
        return;
      }
      // Basic shape tools: press + drag to size with a live preview.
      if (isShapeTool) {
        shapeDraw.onPointerDown(e);
        return;
      }
      // Hold Space (or middle mouse) to pan; otherwise marquee-select.
      if (spaceRef.current || e.button === 1) {
        startPan(e);
        return;
      }
      startMarquee(e);
    },
    [cropId, tool, freeDraw, penTool, startPan, startMarquee, isShapeTool, shapeDraw, getSvgPoint, onTextCreate]
  );

  const handleSurfacePointerMove = useCallback(
    (e) => {
      if (tool === "pencil") freeDraw.onPointerMove(e);
      else if (tool === "pen") penTool.onPointerMove(e);
      else if (isShapeTool) shapeDraw.onPointerMove(e);
    },
    [tool, freeDraw, penTool, isShapeTool, shapeDraw]
  );

  const handleSurfacePointerUp = useCallback(
    (e) => {
      if (tool === "pencil") freeDraw.onPointerUp(e);
      else if (isShapeTool) shapeDraw.onPointerUp(e);
    },
    [tool, freeDraw, isShapeTool, shapeDraw]
  );

  const handleSurfaceDoubleClick = useCallback(
    (e) => {
      // Double-clicking empty canvas exits crop mode.
      if (cropId) {
        onExitCrop?.();
        return;
      }
      if (tool === "pen") penTool.onDoubleClick(e);
    },
    [tool, penTool, cropId, onExitCrop]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const { x, y } = getSvgPoint(e.clientX, e.clientY);
      // Native file drop (image dragged from the OS file manager).
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type && file.type.startsWith("image/")) {
        onCanvasDrop?.({ kind: "file", file }, { x, y });
        return;
      }
      const data = e.dataTransfer.getData("application/x-svg-studio");
      if (!data) return;
      onCanvasDrop?.(JSON.parse(data), { x, y });
    },
    [getSvgPoint, onCanvasDrop]
  );

  const selectedSet = new Set(selectedIds);
  const selectedEls = elements.filter((el) => selectedSet.has(el.id));
  const singleSelected = selectedEls.length === 1 ? selectedEls[0] : null;
  const editing = !!editingTextEl;
  // While editing a text in place, hide the underlying SVG <text> so only the
  // editable overlay shows (no double rendering). Draft text isn't in elements
  // yet, so this only affects the existing-element edit case.
  const renderEls = editing ? elements.filter((el) => el.id !== editingTextEl.id) : elements;
  const cursorClass = tool === "pencil" ? "cursor-pencil" : tool === "pen" ? "cursor-pen" : isShapeTool ? "cursor-crosshair" : "";

  // The dotted grid pans & scales with the view so the surface reads as one
  // continuous, infinite plane rather than a fixed background. A non-transparent
  // export background tints the whole surface so it reads as a single canvas.
  const gridStyle = {
    backgroundPosition: `${view.tx}px ${view.ty}px`,
    backgroundSize: `${GRID * view.scale}px ${GRID * view.scale}px`,
    ...(background && background !== "transparent" ? { backgroundColor: background } : {}),
  };

  return (
    <div
      ref={viewportRef}
      data-testid="canvas-area"
      className={`canvas-surface absolute inset-0 overflow-hidden ${dragOver ? "canvas-surface--drag" : ""} ${cursorClass}`}
      style={gridStyle}
      onPointerDown={handleBgPointerDown}
      onPointerMove={handleSurfacePointerMove}
      onPointerUp={handleSurfacePointerUp}
      onDoubleClick={handleSurfaceDoubleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        className={`canvas-artboard absolute inset-0 ${playing ? "" : "paused"}`}
        style={{ overflow: "visible", touchAction: "none" }}
        data-testid="canvas-svg"
      >
        {/* Transformed content group: holds the artboard, all elements and the
            selection overlays. Pan/zoom is a single transform here. */}
        <g
          ref={svgRef}
          transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}
          data-testid="canvas-content"
        >
          <ElementsLayer
            elements={renderEls}
            selectedId={singleSelected?.id}
            onPointerDown={handleElementPointerDown}
            onDoubleClick={handleElementDoubleClick}
            replayNonce={replayNonce}
            generatingIds={generatingIds}
            croppingId={cropId}
          />

          {/* In-canvas text editor (click-to-insert / double-click to edit) */}
          {editingTextEl && (
            <TextEditOverlay element={editingTextEl} onCommit={onTextCommit} />
          )}

          {/* Live drawing previews */}
          {freeDraw.livePath && <path d={freeDraw.livePath} className="live-draw-path" />}
          {penTool.livePath && (
            <>
              <path d={penTool.livePath} className="live-draw-path" />
              {penTool.points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={3 / view.scale} fill="#FF6B00" stroke="#0a0a0a" strokeWidth={1 / view.scale} />
              ))}
            </>
          )}

          {/* Live shape-draw preview (reduced opacity while dragging) */}
          {shapeDraw.preview && (
            <g opacity={0.45} style={{ pointerEvents: "none" }} data-testid="shape-draw-preview">
              <ElementsLayer elements={[shapeDraw.preview]} />
            </g>
          )}

          {/* Marquee selection rectangle */}
          {marquee && (
            <rect
              x={marquee.x}
              y={marquee.y}
              width={marquee.w}
              height={marquee.h}
              className="marquee-rect"
              data-testid="marquee-rect"
            />
          )}

          {/* Selection overlays (hidden while cropping an image — the crop
              overlay replaces the selection box in that mode). */}
          {!editing && !cropId && !isDrawTool && !isShapeTool && singleSelected && isLineEditable(singleSelected) && (
            <LineEditOverlay
              element={singleSelected}
              scale={view.scale}
              getSvgPoint={getSvgPoint}
              onLiveUpdate={onLiveUpdateLine}
              onResizeStart={onResizeStart}
            />
          )}
          {!editing && !cropId && !isDrawTool && !isShapeTool && singleSelected && !isLineEditable(singleSelected) && (
            <SelectionOverlay
              element={singleSelected}
              scale={view.scale}
              getSvgPoint={getSvgPoint}
              onLiveUpdate={onLiveUpdate}
              onResizeStart={onResizeStart}
            />
          )}
          {!editing && !cropId && !isDrawTool && !isShapeTool && selectedEls.length > 1 && (
            <MultiSelectionOverlay
              elements={selectedEls}
              scale={view.scale}
              getSvgPoint={getSvgPoint}
              onLiveUpdateMany={onLiveUpdateMany}
              onResizeStart={onResizeStart}
              onSelect={onSelect}
            />
          )}

          {/* Image crop overlay: shown when an image is in crop mode. The full
              image keeps rendering (so the user sees the original content to
              crop against); this overlay draws the darkened mask + drag handles. */}
          {cropId && singleSelected && singleSelected.id === cropId && (
            <ImageCropOverlay
              element={singleSelected}
              scale={view.scale}
              getSvgPoint={getSvgPoint}
              onLiveUpdate={onLiveUpdate}
              onResizeStart={onResizeStart}
            />
          )}
        </g>
      </svg>

      <div className="absolute bottom-9 left-3 right-3 z-20 flex items-center justify-between gap-3 pointer-events-none">
        <div className="pointer-events-auto shrink-0 flex items-center gap-1 bg-panel-glass border border-line backdrop-blur" onPointerDown={(e) => e.stopPropagation()}>
          <button onClick={() => zoomAt(0.89)} data-testid="zoom-out-btn" className="h-8 w-8 flex items-center justify-center text-ink2 hover:bg-hover hover:text-ink">
            <ZoomOut className="h-4 w-4" />
          </button>
          <span data-testid="zoom-level" className="text-[11px] font-mono text-ink2 w-12 text-center tabular-nums">
            {Math.round(view.scale * 100)}%
          </span>
          <button onClick={() => zoomAt(1.12)} data-testid="zoom-in-btn" className="h-8 w-8 flex items-center justify-center text-ink2 hover:bg-hover hover:text-ink">
            <ZoomIn className="h-4 w-4" />
          </button>
          <div className="w-px h-5 bg-hover" />
          <button
            onClick={onToggleExpand}
            data-testid="canvas-expand-btn"
            title={canvasExpanded ? "Restore side panels" : "Expand canvas — collapse side panels"}
            className={`h-8 w-8 flex items-center justify-center transition-colors ${
              canvasExpanded ? "text-[#FF6B00] hover:text-[#FF6B00]" : "text-ink2 hover:text-ink"
            } hover:bg-hover`}
          >
            {canvasExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>

        <div className="canvas-hint pointer-events-auto min-w-0 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink4 bg-panel-glass border border-line px-2 py-1" onPointerDown={(e) => e.stopPropagation()}>
          <Hand className="h-3 w-3 shrink-0" />
          <span className="truncate">{tool === "pen" ? "Click anchors · Enter to commit · Esc to cancel" : tool === "pencil" ? "Drag to draw freely" : isShapeTool ? "按住拖动绘制图形 · Press & drag to draw" : "Drag empty space to marquee-select · Space+drag to pan · ⌘+scroll to zoom"}</span>
        </div>
      </div>
    </div>
  );
}
